import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CheckCircle2,
  Shield,
  TrendingUp,
  Users,
  Target,
  Zap,
  Award,
  BarChart3,
  Megaphone,
  ShoppingCart,
  FileText,
  ArrowRight,
  Crown,
  Clock,
  DollarSign,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { QuickNavigation } from '../components/QuickNavigation';
export interface VendorsPageProps {
  cityName: string;
  stateName?: string;
  annualSpending: string; // e.g., "$200-300B" (consumer spending, excluding industry)
  processingRate?: string;
  vendorMonthlyFee?: string;
  vendorAnnualFee?: string;
  heroImage?: string;
  communityGridImage?: string;
  canonicalUrl?: string;
  ogImage?: string;
  onFormOpen?: () => void;
  VendorWaitlistFormComponent?: React.ReactNode;
  VendorFAQComponent?: React.ReactNode;
}

/**
 * VendorsPage - Parameterized vendor recruitment page
 *
 * Explains vendor network benefits, pricing, verification requirements, and payment processing.
 * Used by any city to recruit verified local vendors.
 *
 * @example
 * ```tsx
 * <VendorsPage
 *   cityName="Houston"
 *   stateName="Texas"
 *   population={2304580}
 *   annualSpending="$120B"
 *   projectedTreasury="$8M-$12M+"
 *   heroImage="/media_houston/vendor-hero.png"
 *   VendorWaitlistFormComponent={HoustonVendorForm}
 * />
 * ```
 */
export function VendorsPage({
  cityName,
  stateName = '',
  annualSpending,
  // processingRate: Optional string for UI display, avoid hard-coded, numeric processor rates unless signed off by product/legal
  // Default is intentionally non-committal to avoid promising specific processor rates.
  processingRate = 'competitive with Stripe/Square (estimated)',
  vendorMonthlyFee = '$25',
  vendorAnnualFee = '$180',
  heroImage = '/vendor-hero.png',
  communityGridImage = '/community_grid.png',
  canonicalUrl,
  ogImage,
  onFormOpen,
  VendorWaitlistFormComponent,
  VendorFAQComponent,
}: VendorsPageProps) {
  const [showForm, setShowForm] = useState(false);
  const location = useLocation();

  // Calculate treasury: 0.2% of annual spending
  const projectedTreasury = React.useMemo(() => {
    const match = annualSpending.match(/\$(\d+)-(\d+)([BMK])/);
    if (!match) return annualSpending;

    const [_, low, high, unit] = match;
    const multiplier = unit === 'B' ? 1_000_000_000 : unit === 'M' ? 1_000_000 : 1_000;

    const lowValue = parseFloat(low) * multiplier * 0.002;
    const highValue = parseFloat(high) * multiplier * 0.002;

    const formatValue = (val: number) => {
      if (val >= 1_000_000_000) return `$${(val / 1_000_000_000).toFixed(1)}B`;
      if (val >= 1_000_000) return `$${Math.round(val / 1_000_000)}M`;
      return `$${Math.round(val / 1_000)}K`;
    };

    return `${formatValue(lowValue)}-${formatValue(highValue)}`;
  }, [annualSpending]);

  const cityStateDisplay = stateName ? `${cityName}, ${stateName}` : cityName;
  const cityPossessive = cityName.endsWith('s') ? `${cityName}'` : `${cityName}'s`;

  // Scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Auto-open form if navigated from header "Join Waitlist" dialog
  useEffect(() => {
    if (location.state?.openForm) {
      handleFormOpen();
    }
  }, [location]);

  const handleFormOpen = () => {
    setShowForm(true);
    if (onFormOpen) onFormOpen();
    // Delay scroll to allow form to render
    setTimeout(() => {
      const formElement = document.getElementById('vendor-waitlist-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const scrollToApplication = () => {
    handleFormOpen();
  };

  // Format processingRate for UI display. Append '(estimated)' if it's a numeric percent.
  const processingRateLabel = (rate?: string) => {
    if (!rate) return 'competitive with Stripe/Square (estimated)';
    // If the string contains a percentage like '2.1%' or '~2.9%', treat as numeric and mark estimated
    const isPercent = /\d+(?:\.\d+)?\s*%/.test(rate);
    if (isPercent) {
      // Already approximate if starts with ~, just ensure '(estimated)' suffix appears once
      const base = rate.trim();
      return base.includes('(estimated)') ? base : `${base} (estimated)`;
    }
    return rate;
  };

  // Parse numeric percentage string -> decimal (e.g., '~2.9%' -> 0.029 or '2.5% - 2.9%' -> 0.027 as midpoint). Returns null if no numeric rate is present
  const parseRateDecimal = (rate?: string): number | null => {
    if (!rate) return null;
    // Try to match a range like '~2.5% - 2.9%' or '2.5% - 2.9%'
    const rangeMatch = rate.match(/(~)?\s*(\d+(?:\.\d+)?)\s*%\s*-\s*(\d+(?:\.\d+)?)\s*%/);
    if (rangeMatch) {
      const low = parseFloat(rangeMatch[2]) / 100;
      const high = parseFloat(rangeMatch[3]) / 100;
      return (low + high) / 2; // Return midpoint for calculations
    }
    // Try to match a single value like '~2.9%'
    const singleMatch = rate.match(/(~)?\s*(\d+(?:\.\d+)?)\s*%/);
    if (singleMatch) return parseFloat(singleMatch[2]) / 100;
    return null;
  };

  // Parse annual spending strings like '$200-300B' or '$6B' and return min/max numbers
  const parseSpendingRange = (spending?: string) => {
    if (!spending) return null;
    const match = spending.match(/\$(\d+)(?:-(\d+))?([BMK])?/i);
    if (!match) return null;
    const [, lowStr, highStr, unit] = match;
    const multiplier =
      unit === 'B' ? 1_000_000_000 : unit === 'M' ? 1_000_000 : unit === 'K' ? 1_000 : 1;
    const low = parseFloat(lowStr) * multiplier;
    const high = highStr ? parseFloat(highStr) * multiplier : low;
    return { low, high };
  };

  // Format big numbers to $X.YB / $XM / $XK
  const formatBigMoney = (val: number) => {
    if (val >= 1_000_000_000) return `$${(val / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    if (val >= 1_000_000) return `$${Math.round(val / 1_000_000)}M`;
    if (val >= 1_000) return `$${Math.round(val / 1_000)}K`;
    return `$${Math.round(val)}`;
  };

  // Format range for display: if low === high, show single value; otherwise show range
  const formatMoneyRange = (low: number, high: number) => {
    // Use relative tolerance for large numbers to avoid floating point precision issues
    const tolerance = Math.max(1, Math.abs(low) * 0.0001); // 0.01% tolerance
    return Math.abs(low - high) < tolerance
      ? formatBigMoney(low)
      : `${formatBigMoney(low)} - ${formatBigMoney(high)}`;
  };

  // Compute fee and treasury ranges using numeric processingRate if available; fall back to default if not
  const PROCESS_RATE_LOW = 0.025; // 2.5%
  const PROCESS_RATE_HIGH = 0.029; // 2.9%
  const DEFAULT_PROCESSING_RATE = 0.027; // 2.7% (midpoint) fallback for UI math only
  const DEFAULT_TREASURY_SHARE = 0.002; // 0.2% to Community Treasury (of total transaction volume)
  const DEFAULT_ADOPTION = 0.3; // 30% adoption of verified vendors
  const rateDecimal = parseRateDecimal(processingRate) ?? DEFAULT_PROCESSING_RATE;
  const spendingRange = parseSpendingRange(annualSpending);
  const fullPlatformSpendingRange = spendingRange
    ? { low: spendingRange.low, high: spendingRange.high }
    : null; // 100% participation baseline
  // Platform fees apply only to platform transaction volume (adoption * city spending)
  const cityPlatformSpendingRange = spendingRange
    ? { low: spendingRange.low * DEFAULT_ADOPTION, high: spendingRange.high * DEFAULT_ADOPTION }
    : null;
  const cityFeeRange = cityPlatformSpendingRange
    ? {
        low: cityPlatformSpendingRange.low * rateDecimal,
        high: cityPlatformSpendingRange.high * rateDecimal,
      }
    : null;
  const cityTreasuryRange = cityPlatformSpendingRange
    ? {
        low: cityPlatformSpendingRange.low * DEFAULT_TREASURY_SHARE,
        high: cityPlatformSpendingRange.high * DEFAULT_TREASURY_SHARE,
      }
    : null;
  const cityTreasuryAtFullRange = fullPlatformSpendingRange
    ? {
        low: fullPlatformSpendingRange.low * DEFAULT_TREASURY_SHARE,
        high: fullPlatformSpendingRange.high * DEFAULT_TREASURY_SHARE,
      }
    : null;
  // Full baseline processing fees (100% of consumer card volume) using 3.0%-3.5% range
  const fullFeeRange = fullPlatformSpendingRange
    ? {
        low: fullPlatformSpendingRange.low * PROCESS_RATE_LOW,
        high: fullPlatformSpendingRange.high * PROCESS_RATE_HIGH,
      }
    : null;

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/5 via-background to-primary/10 py-16 sm:py-20 md:py-24">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full border bg-background/80 backdrop-blur-sm px-4 py-1.5 text-sm text-muted-foreground mb-6">
              <Shield className="w-4 h-4 text-primary" /> Verified Vendor Network
            </div>
          </div>

          <div className="mt-6 mb-12 flex justify-center relative z-10 max-w-6xl mx-auto">
            <div className="relative rounded-xl overflow-hidden shadow-2xl border border-white/20 bg-background/50 backdrop-blur-sm p-2">
              <img
                src={heroImage}
                alt={`${cityName} verified vendor network marketplace - Connect with verified residents and compete for group contracts`}
                className="rounded-lg w-full object-cover"
                loading="eager"
                width={1200}
                height={675}
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Stop Competing. Start Winning.
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-2 leading-relaxed">
              Be first in line when we launch. Join our verified marketplace and be discoverable to
              residents in deployed communities{stateName && `, not just ${cityName}`}.
            </p>
            <p className="text-base sm:text-lg text-muted-foreground mb-8">
              Win group contracts. Skip the bidding wars. Get found faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={scrollToApplication} className="text-base gap-2">
                Join Vendor Waitlist <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base"
                onClick={() => {
                  const benefitsSection = document.getElementById('vendor-benefits');
                  if (benefitsSection) benefitsSection.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                See How It Works
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section id="vendor-problem" className="py-12 sm:py-16 bg-muted/30">
        <div className="container px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">The Old Way Is Broken</h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              You're competing with <span className="font-semibold">dozens of vendors</span> for
              every job. Customers don't know who to trust. You waste time on leads that go nowhere.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="text-center">
              <CardHeader>
                <Target className="w-8 h-8 mx-auto text-red-500 mb-2" />
                <h3 className="font-semibold">No Visibility</h3>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Customers can't find you. You're buried in Google results and Facebook spam.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <Users className="w-8 h-8 mx-auto text-red-500 mb-2" />
                <h3 className="font-semibold">Bidding Wars</h3>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  You compete against low-ballers who undercut quality to win contracts.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <Clock className="w-8 h-8 mx-auto text-red-500 mb-2" />
                <h3 className="font-semibold">Wasted Time</h3>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Chasing leads, following up, negotiating one-by-one. Hours for a single customer.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Opportunity Section */}
      <section id="vendor-benefits" className="py-12 sm:py-16">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">The Approved Vendor Advantage</h2>
              <p className="text-muted-foreground text-base sm:text-lg">
                When you're verified in our network,{' '}
                <span className="font-semibold text-primary">customers come to you</span>, not the
                other way around.
              </p>
            </div>

            <div className="space-y-6">
              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <ShoppingCart className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Compete for Group Contracts</h3>
                      <p className="text-muted-foreground text-sm sm:text-base">
                        When communities consolidate demand through the platform, you can{' '}
                        <span className="font-semibold">bid on bulk service opportunities</span>:
                        lawn care, oil changes, repairs, renovations. Scale depends on actual
                        community adoption and participation.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <BarChart3 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Bid on Large-Scale Community Jobs
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base">
                        Communities post group service requests -{' '}
                        <span className="font-semibold">
                          900 homes needing HVAC maintenance, 500 lawns needing care, bulk roof
                          repairs
                        </span>
                        . You get notified when opportunities match your services. Submit
                        competitive bids with volume discounts.
                        <span className="font-semibold">
                          {' '}
                          Win one bid, service hundreds of homes.
                        </span>{' '}
                        Instead of 900 residents independently searching for providers (900 separate
                        communications), the community consolidates demand. One contract. One
                        provider. Government-style bidding at the community level.
                        <span className="font-semibold"> Too big for one vendor?</span> Co-partner
                        with other verified vendors to split large-scale contracts.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Community Grid Visual */}
              <div className="my-8 sm:my-12">
                <div className="relative rounded-xl overflow-hidden shadow-xl border border-primary/20">
                  <img
                    src={communityGridImage}
                    alt={`${cityName} vendor opportunity: verified network enabling bulk purchasing power and contract opportunities for local businesses`}
                    className="w-full object-cover"
                    loading="lazy"
                    width={1200}
                    height={800}
                  />
                </div>
                <p className="text-center text-sm text-muted-foreground mt-4 italic">
                  One network. Thousands of verified homes. Your services at scale.
                </p>
              </div>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <Megaphone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        In-App Marketing That Actually Works
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base">
                        Residents browse approved vendors{' '}
                        <span className="font-semibold">inside the Community App</span>. No Google
                        Ads. No Facebook spam. Just verified vendors with trust badges, and you're
                        one of them. Your services show up when residents search for what you do
                        when they are looking.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <Zap className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Skip the Line: Customers Find You Faster
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base">
                        Residents post service requests in the app. You get notified. You respond.
                        No more cold calling, no more door knocking. They're{' '}
                        <span className="font-semibold">already looking</span>, you just show up
                        verified and ready to serve.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <Award className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Enterprise-Grade Verification = Instant Credibility
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base">
                        Every vendor undergoes{' '}
                        <span className="font-semibold">
                          mandatory institutional-grade KYC/KYB verification
                        </span>{' '}
                        through a
                        <span className="font-semibold">
                          {' '}
                          SOC 2 and ISO 27001 certified third-party provider
                        </span>{' '}
                        trusted by global payment platforms (processing 10M+ users across 65+
                        countries), FCA-regulated financial institutions, and major crypto
                        exchanges. Your government-issued ID and business documents go
                        <span className="font-semibold">
                          {' '}
                          directly to the verification provider
                        </span>
                        , not us. They verify your business against official corporate registries,
                        review formation documents, and validate ownership structures. Once
                        verified, your sensitive documents are securely destroyed per GDPR
                        compliance standards. ToolboxLogic{' '}
                        <span className="font-semibold">never stores</span> your ID or formation
                        documents. Residents know you're legitimate because{' '}
                        <span className="font-semibold">every vendor</span> in the network passed
                        the same rigorous verification. You're not just another directory listing -
                        you're a <span className="font-semibold">trusted, vetted partner</span> in a
                        verified marketplace.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="vendor-stats" className="py-12 sm:py-16 bg-primary/5">
        <div className="container px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">Verified</div>
                <p className="text-muted-foreground">Marketplace Access</p>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">
                  {vendorMonthlyFee}/mo
                </div>
                <p className="text-muted-foreground">Low Barrier to Entry</p>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">100%</div>
                <p className="text-muted-foreground">KYC-Verified Network</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="vendor-pricing" className="py-12 sm:py-16">
        <div className="container px-4 sm:px-6">
          <div className="max-w-2xl mx-auto">
            <Card className="border-2 border-primary/20">
              <CardHeader className="text-center pb-4">
                <Badge className="mx-auto mb-2 bg-primary/10 text-primary border-primary/20">
                  Simple Pricing
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-bold">
                  {vendorMonthlyFee}/month or {vendorAnnualFee}/year
                </h2>
                <p className="text-muted-foreground mt-2">
                  Month-to-month or prepaid annual. Cancel anytime (monthly).
                </p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base">
                      Access to group contract opportunities
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base">Verified badge and trust signals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base">In-app marketing and visibility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base">
                      Direct notifications for service requests
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base">KYC verification included</span>
                  </li>
                </ul>
                <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                  <p className="flex items-start gap-2">
                    <DollarSign className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="font-semibold">Why {vendorMonthlyFee}?</span> This covers
                      institutional-grade KYC/KYB verification through a
                      <span className="font-semibold"> SOC 2 and ISO 27001 certified provider</span>{' '}
                      trusted by global payment platforms and FCA-regulated financial institutions,
                      data storage, platform maintenance, and verified marketplace infrastructure.
                      Your verification is processed by a third-party enterprise provider - your
                      sensitive documents are never stored by ToolboxLogic and are securely
                      destroyed per GDPR standards.
                      <span className="font-semibold"> Refunds:</span> First month is non-refundable
                      once verification is completed. No free trial. Annual prepayment is
                      non-refundable.
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Payment Processing Section */}
      <section id="vendor-payment" className="py-12 sm:py-16 bg-muted/30">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="mx-auto mb-4 bg-green-600/10 text-green-700 border-green-600/20">
                Payment Processing
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Same Cost. Local Benefit.</h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                Your payment processing fees stay{' '}
                <span className="font-semibold text-primary">competitive with Stripe/Square</span>.
                The difference? Part of that money funds the Community Treasury instead of leaving{' '}
                {cityName}.
              </p>
            </div>

            {/* Comparison Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Current Setup */}
              <Card className="border-2 border-muted">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold">Current Setup</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold">•</span>
                      <span>
                        You pay{' '}
                        <span className="font-semibold text-foreground">
                          ~2.5% - 2.9% + $0.30/transaction
                        </span>{' '}
                        to Stripe/Square/etc.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold">•</span>
                      <span>
                        That money{' '}
                        <span className="font-semibold text-foreground">leaves {cityName}</span>{' '}
                        (goes to payment companies)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold">•</span>
                      <span>
                        You get <span className="font-semibold text-foreground">nothing back</span>
                      </span>
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    {fullPlatformSpendingRange && (
                      <div>
                        <p className="text-xs font-semibold text-red-900">
                          City-wide consumer spending:
                          <br />
                          {annualSpending} annually (excl. heavy industry)
                        </p>
                        <p className="text-xs text-red-900 mt-2">
                          100% baseline volume:
                          <br />
                          {formatMoneyRange(
                            fullPlatformSpendingRange.low,
                            fullPlatformSpendingRange.high,
                          )}
                        </p>
                        {fullFeeRange && (
                          <p className="text-xs text-red-900 mt-2">
                            Processing fees (2.5% - 2.9% of baseline):
                            <br />
                            {formatMoneyRange(fullFeeRange.low, fullFeeRange.high)} → paid to
                            Stripe/Square/etc.
                          </p>
                        )}
                        <p className="text-xs text-red-900 mt-2">
                          Community benefit:
                          <br />
                          $0 (fees currently all leave {cityName})
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Verified Vendor Network */}
              <Card className="border-2 border-primary/30 bg-primary/5">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold">Verified Vendor Network</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        You pay{' '}
                        <span className="font-semibold text-foreground">
                          {processingRateLabel(processingRate)}
                        </span>{' '}
                        (competitive with current fees)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Part of that{' '}
                        <span className="font-semibold text-foreground">stays in {cityName}</span>{' '}
                        (Community Treasury + City Revenue)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Opportunity to{' '}
                        <span className="font-semibold text-foreground">
                          bid on treasury-funded projects
                        </span>
                      </span>
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    {cityFeeRange && cityTreasuryRange && (
                      <div>
                        <p className="text-xs font-semibold text-green-900">
                          Example: 30% vendor adoption scenario
                        </p>
                        <p className="text-xs text-green-900 mt-2">
                          Platform processed volume:
                          <br />
                          {formatMoneyRange(
                            cityPlatformSpendingRange!.low,
                            cityPlatformSpendingRange!.high,
                          )}{' '}
                          (30% of {annualSpending})
                        </p>

                        {/*<p className="text-xs text-green-900 mt-2 font-semibold">ToolboxLogic does NOT receive the {Math.round(rateDecimal * 100 * 10) / 10}% processing fees</p>*/}

                        <p className="text-xs text-green-900 mt-2">
                          City revenue (0.2%):
                          <br />
                          {formatMoneyRange(cityTreasuryRange.low, cityTreasuryRange.high)} stays in{' '}
                          {cityName}
                        </p>
                        <p className="text-xs text-green-900 mt-2">
                          Community Treasury (0.2%):
                          <br />
                          {formatMoneyRange(cityTreasuryRange.low, cityTreasuryRange.high)} stays in{' '}
                          {cityName}
                        </p>
                        <div className="my-2 border-t border-green-300"></div>
                        <p className="text-xs text-green-900 mt-2 italic">
                          At 100% participation:
                          <br />
                          {formatMoneyRange(
                            cityTreasuryAtFullRange!.low,
                            cityTreasuryAtFullRange!.high,
                          )}{' '}
                          per fund (city + treasury)
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Key Message */}
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Award className="w-10 h-10 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      The Cost Is the Same. The Difference Is WHERE the Money Goes.
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                      We use{' '}
                      <span className="font-semibold text-foreground">
                        enterprise-grade payment infrastructure
                      </span>{' '}
                      trusted by companies processing{' '}
                      <span className="font-semibold text-primary">
                        hundreds of billions annually
                      </span>{' '}
                      (Uber, eBay, Spotify-scale platforms). This gives us institutional pricing
                      that keeps your costs competitive with Stripe/Square.
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      <span className="font-semibold text-foreground">The benefit to you:</span>{' '}
                      Your payment fees help build the Community Treasury, which funds local
                      infrastructure projects. When the community votes to renovate a park, repair
                      sidewalks, or upgrade facilities,
                      <span className="font-semibold text-primary">
                        {' '}
                        verified local vendors can bid on those contracts
                      </span>
                      . You get the opportunity to compete for projects funded by the treasury you
                      helped build.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      <strong>Clarification:</strong> The 2.5% - 2.9% processing fees are paid
                      directly to third-party payment processors (like Stripe/Square). ToolboxLogic
                      does NOT receive these processing fees. The city receives 0.2% and community
                      treasury receives 0.2% as separate revenue shares - these are the ONLY fees
                      that stay local. ToolboxLogic operates on a separate platform subscription
                      model, not the payment processing fees.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Infrastructure Credibility */}
            <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Enterprise Payment Infrastructure
              </h4>
              <p className="text-sm text-blue-800 mb-3 leading-relaxed">
                Our payment processor powers commerce for global platforms serving millions of users
                across 100+ countries. The same infrastructure that handles{' '}
                <span className="font-semibold">$100B+ in annual transaction volume</span> for
                enterprise clients ensures your payments are secure, compliant, and reliable.
              </p>
              <ul className="space-y-1 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                  <span>
                    <span className="font-semibold">PCI DSS Level 1 compliant</span> (highest
                    security standard)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                  <span>
                    <span className="font-semibold">SOC 2 Type II certified</span> (audited security
                    controls)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                  <span>
                    Trusted by <span className="font-semibold">Fortune 500 companies</span> and{' '}
                    <span className="font-semibold">global marketplaces</span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                  <span>
                    Processes payments for platforms with{' '}
                    <span className="font-semibold">10M+ active users</span>
                  </span>
                </li>
              </ul>
            </div>

            {/* Disclaimer */}
            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                <span className="font-semibold">Important:</span> Payment processing rates are
                subject to negotiation and volume-based pricing with our enterprise processor. The{' '}
                {processingRateLabel(processingRate)} rate is an estimated competitive benchmark.
                Actual rates depend on transaction volume, average ticket size, card mix (debit vs
                credit), and processor contract terms. As the network scales to multiple cities,
                bulk volume may enable lower rates for all vendors. Payment infrastructure is
                provided by a third-party enterprise processor - ToolboxLogic does not handle or
                store customer payment data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Verified Marketplace Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-primary/10 via-background to-accent/5">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="mx-auto mb-4 bg-primary/10 text-primary border-primary/20">
                Two-Sided Verified Marketplace
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Not Just Vendors. Everyone.</h2>
              <p className="text-muted-foreground text-base sm:text-lg">
                This isn't Facebook or the Wild-West-Web. This isn't a random directory. This is a{' '}
                <span className="font-semibold text-primary">verified marketplace</span> where
                <span className="font-semibold"> both vendors AND residents</span> are
                authenticated.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-primary/20">
                <CardContent className="pt-6">
                  <Shield className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Vendors: Enterprise-Grade KYC/KYB</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    <span className="font-semibold">Mandatory verification.</span> Government-issued
                    ID, business formation documents, corporate registry checks, ownership structure
                    validation, biometric authentication. Processed by a SOC 2 and ISO 27001
                    certified provider trusted by FCA-regulated financial institutions and global
                    payment platforms.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Your privacy protected:</span> Documents go
                    directly to third-party verifier, then destroyed. We never store your sensitive
                    data. Data encrypted at rest, audited to GDPR standards. No bots. Only verified
                    businesses.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-primary/20">
                <CardContent className="pt-6">
                  <Users className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    Residents: PCI-Certified Identity Verification
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    <span className="font-semibold">Residents verify too.</span> Government-issued
                    ID verification (100+ countries supported), biometric selfie matching, liveness
                    detection to prevent deepfakes. Processed by a{' '}
                    <span className="font-semibold">
                      PCI DSS Level 1 and SOC 2 certified provider
                    </span>{' '}
                    that powers identity verification for global payment infrastructure processing
                    hundreds of billions annually.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Privacy first:</span> Resident data encrypted at
                    rest. No bots. No scammers. No random accounts. Result: Professional commerce
                    environment with real, verified customers.
                  </p>
                </CardContent>
              </Card>
            </div>
            <div className="mt-8 bg-muted/50 rounded-lg p-6 border border-primary/10">
              <p className="text-sm text-center text-muted-foreground">
                <span className="font-semibold text-foreground">
                  Why the verification requirement?
                </span>{' '}
                Quality over quantity. We'd rather have a verified marketplace with
                <span className="font-semibold"> authenticated participants</span> than thousands of
                unverified accounts and Wild West chaos. This isn't optional - if you don't want to
                verify, this platform isn't for you. Go to Facebook if you want unverified commerce.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-12 sm:py-16 bg-muted/30">
        <div className="container px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
              Mandatory Verification Requirements
            </h2>
            <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
              Every vendor must complete these steps.{' '}
              <span className="font-semibold">No exceptions.</span> No bots. No fake accounts. No
              random vendors. This is what separates us from unverified marketplaces.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <Shield className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Enterprise KYC/KYB Verification</h3>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Required.</span> Corporate registry validation
                    across 30K+ global data sources (220+ countries), Ultimate Beneficial Owner
                    (UBO) verification, AML screening against 11,000+ watchlists/PEP databases,
                    business formation documents review, ownership structure validation, biometric
                    authentication.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <FileText className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Business License Validation</h3>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Required.</span> Active business license
                    verified against state/city registries. Unlicensed vendors are rejected during
                    verification.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <BarChart3 className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Marketplace Policies Agreement</h3>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Required.</span> Agree to community standards,
                    service quality expectations, dispute resolution process, and professional
                    conduct guidelines.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Crown className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Professional Service Standards</h3>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Required.</span> Maintain minimum 3.5-star
                    rating, 24-hour response time, 80%+ contract completion rate. Quality control is
                    enforced.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor FAQ Section */}
      {VendorFAQComponent && <section id="vendor-faq">{VendorFAQComponent}</section>}

      {/* Application CTA Section */}
      <section
        id="vendor-application"
        className="py-16 sm:py-20"
        style={{ background: 'linear-gradient(to bottom right, #1e3a8a, #172554, #020617)' }}
      >
        <div className="container px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Ready to Be First in Line?
            </h2>
            <p className="text-lg sm:text-xl mb-8 text-white/90">
              Join the vendor waitlist today. We'll reach out when the platform launches so you can
              complete verification and go live in deployed communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="text-base gap-2 !bg-white !text-primary hover:!bg-white/90 hover:!text-primary"
                onClick={scrollToApplication}
              >
                Join Vendor Waitlist <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-white/80 mt-6">
              We're collecting early interest. You'll be notified when we launch and applications
              open.
            </p>
          </div>
        </div>
      </section>

      {/* Vendor Waitlist Form Section - Only render when form is active */}
      {showForm && VendorWaitlistFormComponent && (
        <section id="vendor-waitlist-form" className="py-12 sm:py-16 bg-muted/30">
          <div className="container px-4 sm:px-6">{VendorWaitlistFormComponent}</div>
        </section>
      )}

      <QuickNavigation />
    </>
  );
}
